import type { BookingCounts, BookingState } from '../hooks/use-booking-status';

/**
 * "{count} trades still booking", with the language file's own singular/plural
 * convention: a `...One` key for exactly one, `{count}` replaced otherwise (see
 * `portfolio.previewResultCount` / `previewResultCountOne`). A partial count carries a
 * `+` suffix — it is a lower bound, so it always reads as a plural. Only a cursor makes
 * a count partial; a failed import read leaves every count exact and is named on its
 * own (`importsUnchecked`), never as a `+`.
 */
export function bookingCountLabel(
  t: (key: string) => string,
  key: string,
  count: number,
  isPartial: boolean,
): string {
  if (count === 1 && !isPartial) return t(`${key}One`);
  return t(key).replace('{count}', isPartial ? `${count}+` : String(count));
}

export type BookingCategory = 'trades' | 'snapshots' | 'imports' | 'uncounted' | 'importsUnchecked';

/**
 * What a booking sentence names, in the order it names them: the non-zero counts —
 * unbooked trades, then unsettled snapshots, then running imports — or, when every
 * count is zero yet `isSettlingCounts` still called the book settling, the uncounted
 * case: some read carried a cursor, so the zero is an unproven lower bound, not a
 * settled book. `uncounted` is reserved for that cursor-only case; it never stands in
 * for a failed read.
 *
 * `importsUnchecked` is a caveat, not a count: present whenever a running-import read
 * failed this tick, whatever the counts say, because the running-import figure is then
 * missing whatever that read would have returned. It follows every other category, so
 * a settling book reads "3 trades still booking. Running imports could not be checked."
 * and a book with nothing else to name (the `incomplete` state) opens on it alone.
 *
 * `BookingMarker`'s `bookingParts` and this file's own `emptyPositionsCopy` both cascade
 * through this one order; sharing it here is what keeps them from drifting apart the
 * moment a category is added and only one of the two call sites is updated.
 */
export function bookingCategoriesInOrder(counts: BookingCounts): BookingCategory[] {
  const categories: BookingCategory[] = [];
  if (counts.unbookedTradeCount > 0) categories.push('trades');
  if (counts.unsettledSnapshotCount > 0) categories.push('snapshots');
  if (counts.runningImportCount > 0) categories.push('imports');
  if (categories.length === 0 && counts.isPartial) categories.push('uncounted');
  if (counts.failedImportReadCount > 0) categories.push('importsUnchecked');
  return categories;
}

/**
 * The base sentence for the first category `bookingCategoriesInOrder` names, exhaustive
 * over `BookingCategory` rather than a ternary whose `else` would otherwise catch
 * anything unhandled — which, for `emptyPositionsCopy`'s caller, includes
 * `importsUnchecked` and `undefined`. Both are asserted unreachable here
 * (`emptyPositionsCopy` only ever runs for a settling book, where the first category is
 * a count or the uncounted case — see its own doc comment below), but the assertion
 * belongs in code, not only in a comment: a ternary's `else` would otherwise silently
 * render the pagination-flavored `uncounted` copy for a failure that has nothing to do
 * with pagination, the moment that invariant ever breaks.
 */
function bookingCategoryBaseSentence(
  t: (key: string) => string,
  category: BookingCategory | undefined,
  counts: BookingCounts,
): string {
  switch (category) {
    case 'trades':
      return bookingCountLabel(t, 'portfolio.positionsBookingCount', counts.unbookedTradeCount, counts.isPartial);
    case 'snapshots':
      return bookingCountLabel(
        t, 'portfolio.positionsSnapshotsSettling', counts.unsettledSnapshotCount, counts.isPartial,
      );
    case 'imports':
      return bookingCountLabel(t, 'portfolio.positionsImportRunning', counts.runningImportCount, counts.isPartial);
    case 'uncounted':
      return `${t('portfolio.booking.uncounted')}.`;
    default:
      throw new Error(
        `bookingCategoryBaseSentence: "${category ?? 'no category'}" is a caveat or absent, not a `
        + 'count, and cannot open a settling sentence',
      );
  }
}

/**
 * The empty positions table's copy while the book is settling, slow, or timed out — never
 * called for `unknown`, which has its own card, for `incomplete`, whose trade and
 * snapshot reads proved the table genuinely empty and which renders the plain empty
 * card, or for the settled table, which has no marker at all.
 *
 * One of four base sentences, chosen by the first category `bookingCategoriesInOrder`
 * names — the same order `BookingMarker`'s `bookingParts` cascades through, so the two
 * can never disagree on which category a book in this state is actually waiting on. In
 * every state this function accepts the book is settling, so that first category is a
 * count or the uncounted case, never the `importsUnchecked` caveat, which cannot open a
 * sentence here (see `bookingCategoryBaseSentence`, which throws if that invariant ever
 * breaks). The uncounted case (every count zero, some read still partial) is the
 * only way this function runs with every count at zero; the same copy the marker
 * renders for it (`portfolio.booking.uncounted`).
 *
 * Every counted branch, including the import one, goes through `bookingCountLabel` — the
 * same helper the marker's `bookingParts` uses — so this card's sentence can never show a
 * fixed, uncounted "an import is still running" beside the marker's own counted "2
 * imports running" for the identical fact.
 *
 * When a running-import read failed this tick, the caveat the marker names follows the
 * base sentence here too (`portfolio.booking.importsUnchecked`), so the card cannot read
 * as a complete account of the book above a marker that says it is not.
 *
 * The tail then says what to expect next, and must never promise the page keeps
 * refreshing once it has stopped: settling and slow both still auto-refresh, so they
 * reuse the marker's own tails (`portfolio.booking.tail` / `.slow`); timed-out uses its
 * own copy naming the Check again button above instead.
 */
export function emptyPositionsCopy(
  t: (key: string) => string,
  state: Exclude<BookingState, 'unknown' | 'incomplete'>,
  counts: BookingCounts,
): string {
  const categories = bookingCategoriesInOrder(counts);
  const [category] = categories;
  const base = bookingCategoryBaseSentence(t, category, counts);
  const caveat = categories.includes('importsUnchecked')
    ? `${t('portfolio.booking.importsUnchecked')}. `
    : '';
  const tail = state === 'timed-out'
    ? t('portfolio.positionsCheckStopped')
    : state === 'slow'
      ? t('portfolio.booking.slow')
      : t('portfolio.booking.tail');
  return `${base} ${caveat}${tail}`;
}
